import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PersonStatus, isAdmin, type DeletedPersonDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';
import { PersonCell } from '../components/PersonCell';

/** dd/mm/yyyy hh:mm from an ISO timestamp. */
function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Admin-only bin of soft-deleted collaborators, with restore. */
export function DeletedPeople() {
  const { user } = useAuth();
  const t = useT();
  const admin = isAdmin(user?.roles ?? []);
  const [rows, setRows] = useState<DeletedPersonDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) {
      setLoading(false);
      return;
    }
    api
      .listDeletedPeople()
      .then(setRows)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [admin]);

  async function restore(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.restorePerson(id);
      setRows((r) => r.filter((p) => p.id !== id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('people.deleted.title')}</h2>
          <p className="page-sub">{t('people.deleted.subtitle')}</p>
        </div>
        <div className="spacer" />
        <Link to="/people">
          <button type="button" className="secondary">
            {t('people.deleted.back')}
          </button>
        </Link>
      </div>

      {error && <div className="alert error">{error}</div>}

      {!admin ? (
        <p className="muted">{t('people.deleted.adminOnly')}</p>
      ) : (
        <div className="erp-panel">
          {loading ? (
            <div className="panel-empty">{t('common.loading')}</div>
          ) : rows.length === 0 ? (
            <div className="panel-empty is-empty">{t('people.deleted.empty')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>{t('people.deleted.col.code')}</th>
                    <th>{t('people.deleted.col.name')}</th>
                    <th>{t('people.deleted.col.state')}</th>
                    <th>{t('people.deleted.col.created')}</th>
                    <th>{t('people.deleted.col.deleted')}</th>
                    <th>{t('people.deleted.col.by')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id}>
                      <td>{p.code ?? '—'}</td>
                      <td>
                        <PersonCell name={p.fullName}>{p.fullName}</PersonCell>
                      </td>
                      <td>
                        {p.status === PersonStatus.Active
                          ? t('shared.active')
                          : t('shared.inactive')}
                      </td>
                      <td>{fmt(p.createdAt)}</td>
                      <td>{fmt(p.deletedAt)}</td>
                      <td>{p.deletedBy ?? '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => void restore(p.id)}
                          disabled={busyId === p.id}
                        >
                          {busyId === p.id ? '…' : t('people.deleted.restore')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
